import {
  enterFullscreen, exitFullscreen, fullscreenActive, fullscreenTarget, lockLandscape,
  onFullscreenChange, unlockOrientation, type FullscreenRoute
} from './fullscreen';

/** Touch-first phones/tablets, including iPads using a desktop user agent.
 * No width cutoff: an iPad Pro in landscape is wider than many laptops.
 */
export function prefersLandscapePlayback(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ipad = navigator.maxTouchPoints > 1 && /Macintosh|MacIntel/.test(navigator.userAgent);
  return ipad || (typeof matchMedia === 'function' &&
    matchMedia('(hover: none) and (pointer: coarse)').matches);
}

// A retiring player must not unlock a newer player's orientation request.
let orientationOwner: symbol | null = null;

/** Local presentation only. Never observes room state or native `play` events.
 * Call userPlayback/enter directly in a click/key handler, never after awaiting
 * media loading, navigation, play(), or a room acknowledgement.
 */
export class PlaybackPresentation {
  private disposed = false;
  private revision = 0;
  private pending = false;
  private wanted = false;
  private lockToken: symbol | null = null;
  private readonly unsubscribe: () => void;

  constructor(
    private readonly stage: HTMLElement,
    private video: HTMLVideoElement | null,
    private readonly mobile: () => boolean = prefersLandscapePlayback,
    private readonly changed: (active: boolean) => void = () => {}
  ) {
    this.stage = stage = fullscreenTarget(stage)!;
    this.wanted = fullscreenActive(stage, video);
    this.unsubscribe = onFullscreenChange(stage, video, (active) => {
      // The route may enter while this player's lazy import is settling.
      if (active && this.revision === 0) this.wanted = true;
      if (!active) this.cancel();
      // Older WebKit returns void before the fullscreen-change event arrives.
      // Retry orientation only when that event confirms our element is fullscreen.
      else if (!this.wanted && this.revision > 0) {
        void exitFullscreen(stage, video);
        return;
      } else if (this.wanted && !this.pending &&
        (document.fullscreenElement === stage ||
          (document as Document & { webkitFullscreenElement?: Element }).webkitFullscreenElement === stage)) {
        void this.ensureLandscape(this.revision);
      }
      this.changed(active);
    });
    if (this.wanted) {
      this.changed(true);
      void this.ensureLandscape(this.revision);
    }
  }

  /** Attach the lazy player's native-video route without creating another owner. */
  bindVideo(video: HTMLVideoElement, changed: (active: boolean) => void): () => void {
    this.video = video;
    const stop = onFullscreenChange(this.stage, video, changed);
    changed(fullscreenActive(this.stage, video));
    return () => {
      stop();
      // Native Safari fullscreen belongs to this video, not to the DOM root.
      // Detaching media must close it, but must not exit root element fullscreen.
      void exitFullscreen(null, video);
      if (this.video === video) this.video = null;
    };
  }

  /** Preserve the transport call and its activation before fullscreen consumes it. */
  userPlayback(action: () => void, startsPlayback: boolean): void {
    if (!startsPlayback && this.pending) this.cancel();
    action();
    if (startsPlayback && this.mobile()) void this.enter(false);
  }

  async enter(explicit = true): Promise<FullscreenRoute> {
    if (this.disposed || this.pending || fullscreenActive(this.stage, this.video)) return 'none';
    this.pending = true;
    this.wanted = true;
    const revision = ++this.revision;
    // enterFullscreen invokes the browser API synchronously before its first await.
    // Automatic playback presentation must not hand a sideways DOM interface to
    // Safari's unrotated platform player. Explicit fullscreen remains a choice.
    const rotated = this.stage.querySelector?.('[data-watch-space][data-sideways="true"]') != null;
    const route = await enterFullscreen(this.stage, this.video, explicit || !rotated);
    try {
      if (route === 'none') this.wanted = false;
      if (this.disposed || revision !== this.revision) {
        if (route !== 'none') await exitFullscreen(this.stage, this.video);
        return 'none';
      }
      if (this.mobile() && (route === 'standard' || route === 'webkit-element') &&
          fullscreenActive(this.stage, this.video)) {
        await this.ensureLandscape(revision);
      }
      return route;
    } finally {
      this.pending = false;
    }
  }

  private locking = false;

  private async ensureLandscape(revision: number): Promise<void> {
    if (this.disposed || this.lockToken !== null || !this.mobile()) return;
    const token = Symbol('player orientation');
    this.lockToken = token;
    orientationOwner = token;
    this.locking = true;
    const locked = await lockLandscape();
    this.locking = false;
    if (this.disposed || revision !== this.revision) {
      // A lock may settle after exit/unmount already tried to release it.
      if (orientationOwner === token) {
        if (locked) unlockOrientation();
        orientationOwner = null;
      }
      this.lockToken = null;
    } else if (!locked && orientationOwner === token) {
      orientationOwner = null;
      this.lockToken = null;
    }
  }

  private cancel(): void {
    this.wanted = false;
    ++this.revision;
    if (this.lockToken !== null && orientationOwner === this.lockToken) {
      unlockOrientation();
      // Keep the token during a pending lock so a late success gets released too.
      if (!this.locking) orientationOwner = null;
    }
    if (!this.locking) this.lockToken = null;
  }

  async exit(): Promise<void> {
    this.cancel();
    await exitFullscreen(this.stage, this.video);
  }

  destroy(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.unsubscribe();
    this.cancel();
    void exitFullscreen(this.stage, this.video);
    if (watchPresentations.get(this.stage) === this) watchPresentations.delete(this.stage);
  }
}

// One lifecycle owner per persistent watch root, created before media loads.
const watchPresentations = new WeakMap<HTMLElement, PlaybackPresentation>();
export function watchPresentation(stage: HTMLElement): PlaybackPresentation | null {
  const root = fullscreenTarget(stage);
  if (!root?.hasAttribute?.('data-watch-viewport')) return null;
  let presentation = watchPresentations.get(root);
  if (!presentation) {
    presentation = new PlaybackPresentation(root, null);
    watchPresentations.set(root, presentation);
  }
  return presentation;
}
